/**
 * VentaFormDialog.js — extracted form dialog for VentasPage.
 *
 * Pure presentation: receives state + handlers from parent. Splitting this
 * out drops VentasPage.js from 771 to ~520 lines and concentrates the
 * passenger / route / commission UI in a single place that can be re-used
 * by future flows (e.g. AI assistant pre-fill).
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Loader2, Pencil, CalendarIcon, X } from 'lucide-react';

export const VentaFormDialog = ({
  open,
  onClose,
  onSubmit,
  editingVenta,
  formData,
  setFormData,
  gestores,
  airlines,
  pricingRoutes,
  submitting,
  addPassenger,
  removePassenger,
  updatePassenger,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-600" />
            {editingVenta ? 'Editar Venta' : 'Registrar Nueva Venta'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-5">
          {/* Gestor */}
          <div className="space-y-2">
            <Label>Gestor de Ventas *</Label>
            <Select value={formData.gestor_id} onValueChange={(value) => setFormData({ ...formData, gestor_id: value })}>
              <SelectTrigger className="bg-slate-50" data-testid="venta-gestor-select">
                <SelectValue placeholder="Selecciona un gestor" />
              </SelectTrigger>
              <SelectContent>
                {gestores.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Aerolínea */}
          <div className="space-y-2">
            <Label>Aerolínea</Label>
            <Select value={formData.airline} onValueChange={(value) => setFormData({ ...formData, airline: value })}>
              <SelectTrigger className="bg-slate-50" data-testid="venta-airline-select">
                <SelectValue placeholder="Seleccionar aerolínea" />
              </SelectTrigger>
              <SelectContent>
                {airlines.map((a) => (
                  <SelectItem key={a.code} value={a.name}>
                    <span className="flex items-center gap-2">
                      <img src={`/airlines/${a.logo}`} alt={a.name} className="w-5 h-5 object-contain" />
                      {a.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* PNR */}
          <div className="space-y-2">
            <Label>Código PNR <span className="text-slate-400">(opcional)</span></Label>
            <Input
              value={formData.pnr_code ?? ''}
              onChange={(e) => setFormData({ ...formData, pnr_code: e.target.value.toUpperCase() })}
              placeholder="ABC123"
              className="bg-slate-50 font-mono"
              data-testid="venta-pnr-input"
            />
          </div>

          {/* Multi-Passenger */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Pasajeros *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPassenger} data-testid="add-passenger-btn">
                <Plus className="w-3 h-3 mr-1" /> Agregar pasajero
              </Button>
            </div>
            <div className="space-y-2">
              {formData.passengers.map((pax, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`pax-row-${index}`} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-5">{index + 1}.</span>
                  <Input
                    value={pax ?? ''}
                    onChange={(e) => updatePassenger(index, e.target.value)}
                    placeholder={`Nombre del pasajero ${index + 1}`}
                    required={index === 0}
                    className="bg-slate-50"
                    data-testid={`passenger-input-${index}`}
                  />
                  {formData.passengers.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removePassenger(index)}>
                      <X className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ruta from Pricing Guides */}
          <div className="space-y-2">
            <Label>Ruta *</Label>
            <Select value={`${formData.route_origin}|${formData.route_destination}`} onValueChange={(value) => {
              const [origin, dest] = value.split('|');
              setFormData({ ...formData, route_origin: origin, route_destination: dest });
            }}>
              <SelectTrigger className="bg-slate-50" data-testid="venta-route-select">
                <SelectValue placeholder="Seleccionar ruta" />
              </SelectTrigger>
              <SelectContent>
                {pricingRoutes.map((r) => {
                  const parts = r.service_name.split(/[-–]/);
                  const origin = parts[0]?.trim() || '';
                  const dest = parts[1]?.trim() || '';
                  return (
                    <SelectItem key={r.id} value={`${origin}|${dest}`}>
                      {r.service_name} — Costo: ${r.provider_cost} | Venta: ${r.selling_price}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {formData.route_origin && formData.route_destination && (() => {
              const matched = pricingRoutes.find(r => {
                const parts = r.service_name.split(/[-–]/);
                return parts[0]?.trim().toLowerCase() === formData.route_origin.toLowerCase() && parts[1]?.trim().toLowerCase() === formData.route_destination.toLowerCase();
              });
              return matched ? (
                <div className="flex gap-3 text-xs p-2 bg-blue-50 rounded-lg border border-blue-100" data-testid="route-pricing-info">
                  <span className="text-red-600">Costo Proveedor: <strong>${matched.provider_cost}</strong></span>
                  <span className="text-blue-600">Precio Venta: <strong>${matched.selling_price}</strong></span>
                  <span className="text-amber-600">Comision: <strong>{matched.commission_pct}%</strong></span>
                  <span className="text-green-600">Ganancia: <strong>${matched.profit_per_unit?.toFixed(2)}</strong></span>
                </div>
              ) : null;
            })()}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fecha de Ida *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-slate-50" data-testid="venta-departure-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.flight_date_departure, 'dd/MM/yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={formData.flight_date_departure} onSelect={(date) => date && setFormData({ ...formData, flight_date_departure: date })} locale={es} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Fecha de Vuelta</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-slate-50" data-testid="venta-return-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.flight_date_return ? format(formData.flight_date_return, 'dd/MM/yyyy') : 'Solo ida'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={formData.flight_date_return} onSelect={(date) => setFormData({ ...formData, flight_date_return: date })} locale={es} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Monto y Pasajes */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cantidad de Pasajes *</Label>
              <Input type="number" min="1" value={formData.tickets_count ?? ''}
                onChange={(e) => setFormData({ ...formData, tickets_count: e.target.value })}
                placeholder="1" required className="bg-slate-50" data-testid="venta-tickets-input" />
            </div>
            <div className="space-y-2">
              <Label>Monto Total (USD) *</Label>
              <Input type="number" min="0" step="0.01" value={formData.amount ?? ''}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="500" required className="bg-slate-50" data-testid="venta-amount-input" />
            </div>
          </div>

          {/* Comision CEO */}
          <div className="space-y-2 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="apply_commission"
                checked={formData.apply_commission}
                onChange={(e) => setFormData({ ...formData, apply_commission: e.target.checked, commission_pct: e.target.checked ? formData.commission_pct : '' })}
                className="w-4 h-4 rounded border-slate-300"
                data-testid="venta-commission-check"
              />
              <Label htmlFor="apply_commission" className="cursor-pointer text-sm">Cobrar comision CEO en esta venta</Label>
            </div>
            {formData.apply_commission && (
              <div className="flex items-center gap-3 mt-2">
                <div className="space-y-1 flex-1">
                  <Label className="text-xs">Comision (%)</Label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={formData.commission_pct ?? ''}
                    onChange={(e) => setFormData({ ...formData, commission_pct: e.target.value })}
                    placeholder="Ej: 2.5"
                    className="bg-white"
                    data-testid="venta-commission-pct"
                  />
                </div>
                {formData.amount && formData.commission_pct && (
                  <div className="text-right pt-4">
                    <p className="text-xs text-slate-500">Comision</p>
                    <p className="font-bold text-amber-700">${(parseFloat(formData.amount || 0) * parseFloat(formData.commission_pct || 0) / 100).toFixed(2)}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={submitting || !formData.gestor_id} className="bg-blue-700 hover:bg-blue-800" data-testid="save-venta-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingVenta ? 'Actualizar Venta' : 'Registrar Venta')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const VentaDeleteDialog = ({ open, onClose, onConfirm, ventaName }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Confirmar eliminación</DialogTitle>
      </DialogHeader>
      <p className="text-slate-600">
        ¿Estás seguro de que deseas eliminar esta venta de <strong>{ventaName}</strong>?
      </p>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          data-testid="confirm-delete-venta-btn"
        >
          Eliminar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
